/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Table as LoftTable,
  Checkbox,
  Spinner,
  Box,
  Flex,
  theme,
  Pagination,
} from '@xometry/xometry_loft';
import {
  usePagination,
  useTable,
  useRowSelect,
  useExpanded,
  Row,
} from 'react-table';
import { css } from '@emotion/react';
import { PAGINATION_PAGE_SIZE } from '../../containers/Organization/TeamSettings/TeamMemberManagement/helper';

interface TableProps {
  data: any;
  columns: any;
  fetchCallback?: (pageIndex: number, pageSize: number) => void;
  recordPageCount?: number;
  loading: boolean;
  hideHeader?: boolean;
  showSelectColumn?: boolean;
  clickableRow?: boolean;
  selectedPage: number;
  setSelectedPage: (value: any) => void;
  selectRows?: (allIds: any[], newIds: any[]) => void;
  disabledCondition?: (input: any) => boolean;
  renderRowSubComponent?: (row: any) => React.ReactNode;
  hiddenColumns?: string[];
  pageSize?: number;
  isReadOnly?: boolean;
  showPagination?: boolean;
}

const IndeterminateCheckbox = React.forwardRef((props: any, ref: any) => {
  const { indeterminate, ...otherProps } = props;
  const checkRef = useRef(null);
  let resolvedRef: any;
  if (ref) {
    resolvedRef = ref;
  } else {
    resolvedRef = checkRef;
  }

  useEffect(() => {
    if (indeterminate && !otherProps.checked && !otherProps.disabled) {
      resolvedRef.current.checked = true;
    } else if (!indeterminate && otherProps.checked && !otherProps.disabled) {
      resolvedRef.current.checked = true;
    } else {
      resolvedRef.current.checked = false;
    }
  }, [otherProps, indeterminate]);

  return (
    <>
      <Checkbox
        enterKeyHint
        ref={resolvedRef}
        {...otherProps}
        checkedIcon={indeterminate ? 'minus' : 'checked'}
      />
    </>
  );
});

const Table = forwardRef((props: TableProps, ref) => {
  const {
    data,
    columns,
    fetchCallback,
    recordPageCount,
    loading,
    hideHeader,
    showSelectColumn,
    clickableRow,
    selectedPage,
    setSelectedPage,
    selectRows,
    disabledCondition = () => false,
    renderRowSubComponent = null,
    hiddenColumns = [],
    pageSize,
    isReadOnly,
    showPagination = true,
  } = props;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: recordPageCount,
      initialState: {
        pageIndex: 0,
        pageSize,
        hiddenColumns,
      } as any,
      autoResetSelectedRows: false,
      getRowId: (row: any) => row.id,
      stateReducer: (newState: any, action: any) => {
        switch (action.type) {
          case 'toggleAllRowsSelected':
            return {
              ...newState,
              selectedRowIds: {},
            };

          default:
            return newState;
        }
      },
    } as any,
    (hooks: any) => {
      if (showSelectColumn && selectRows) {
        /* eslint-disable @typescript-eslint/no-shadow */
        hooks.visibleColumns.push((columns: any) => [
          {
            id: 'selection',
            Header: ({
              page: headerPage,
              getToggleAllPageRowsSelectedProps,
            }: any) => (
              <>
                {!isReadOnly && (
                  <IndeterminateCheckbox
                    {...getToggleAllPageRowsSelectedProps()}
                    disabled={
                      headerPage.filter(
                        (row: any) => !disabledCondition(row.original)
                      ).length === 0 || isReadOnly
                    }
                  />
                )}
              </>
            ),
            Cell: ({ row }: any) => (
              <Box
                onClick={(event: any) => {
                  clickableRow && !isReadOnly && event.stopPropagation();
                }}
              >
                {!isReadOnly && (
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    disabled={disabledCondition(row.original) || isReadOnly}
                  />
                )}
              </Box>
            ),
          },
          ...columns,
        ]);
      }
    },
    useExpanded,
    usePagination,
    useRowSelect
  ) as any;

  useImperativeHandle(ref, () => ({
    resetSelection() {
      toggleAllRowsSelected(false);
    },
  }));

  useEffect(() => {
    if (selectedRowIds && selectRows) {
      selectRows(
        selectedRowIds,
        selectedFlatRows.map((entry: Row) => entry.original)
      );
    }
  }, [selectedRowIds]);

  useEffect(() => {
    fetchCallback && fetchCallback(selectedPage, PAGINATION_PAGE_SIZE);
  }, [fetchCallback, selectedPage]);

  return (
    <>
      {loading ? (
        <Flex minHeight="700px" width="100%" alignItems="center">
          <Spinner scale="full-screen"></Spinner>
        </Flex>
      ) : (
        <>
          <LoftTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any) => {
                const { key: rowKey } = headerGroup.getHeaderGroupProps();
                return (
                  <tr {...headerGroup.getHeaderGroupProps()} key={rowKey}>
                    {headerGroup.headers.map((column: any) => {
                      const { key: headerKey } = column.getHeaderProps();
                      return (
                        <th {...column.getHeaderProps()} key={headerKey}>
                          {!hideHeader && <>{column.render('Header')}</>}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                // eslint-disable-line react/prop-types
                prepareRow(row);
                const { key: rowKey } = row.getRowProps();
                return (
                  <>
                    <tr
                      {...row.getRowProps()}
                      key={rowKey}
                      onClick={() =>
                        showSelectColumn &&
                        clickableRow &&
                        !isReadOnly &&
                        !disabledCondition(row.original) &&
                        row.toggleRowSelected()
                      }
                      css={css`
                        cursor: ${(showSelectColumn &&
                          clickableRow &&
                          !disabledCondition(row.original)) ||
                        !isReadOnly
                          ? 'pointer'
                          : 'initial'};
                      `}
                    >
                      {row.cells.map((cell: any) => {
                        const { key: cellKey } = cell.getCellProps();
                        return (
                          <td {...cell.getCellProps()} key={cellKey}>
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                    {row.isExpanded ? (
                      <>{renderRowSubComponent?.({ row })}</>
                    ) : null}
                  </>
                );
              })}
              {page.length > 0 &&
                pageSize &&
                pageSize - page.length > 0 &&
                Array(pageSize - page.length)
                  .fill(null)
                  .map(() => {
                    const row = page[0];
                    const { key: rowKey } = row;
                    return (
                      <tr {...row.getRowProps()} key={rowKey}>
                        {row.cells.map((cell: any) => {
                          const { key: cellKey } = cell.getCellProps();
                          return (
                            <td {...cell.getCellProps()} key={cellKey}>
                              &nbsp;
                              <br />
                              &nbsp;
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
            </tbody>
          </LoftTable>
          {showPagination && (
            <LoftTable.PaginationArea>
              <Flex alignItems="center" fontSize={theme.fontSizes[0]}>
                Page {selectedPage} of {recordPageCount}
              </Flex>
              <Box>
                <Pagination
                  numberOfPages={recordPageCount}
                  selectedPage={selectedPage}
                  setSelectedPageInParent={(pageNumber: number) => {
                    setSelectedPage(pageNumber);
                  }}
                  navigationArrows={true}
                />
              </Box>
            </LoftTable.PaginationArea>
          )}
        </>
      )}
    </>
  );
});

Table.defaultProps = {
  hideHeader: false,
  showSelectColumn: true,
  clickableRow: false,
  selectRows: () => null,
  disabledCondition: () => false,
};

export default Table;
